import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import {
  Container, Content, Card,
  LoadingContainer,
} from './styles';
import BackArrow from '../../components/BackArrow';

import ChallengeHeader from '../../components/ChallengeHeader';
import ChallengeSubscription from './ChallengeSubscription';
import ChallengeGoal from './ChallengeGoal';
import ChallengeProgress from './ChallengeProgress';
import coreApi from '../../services/coreApi';
import { RootState } from '../../store';
import IntegrationCard from '../../components/IntegrationCard';
import Loader from '../../components/Loader';
import api, { api2 } from '../../services/api';

import { ChallengeDetailType, ChallengeType, Params } from '../../types/Challenger';

export enum ChallengeStatesTypes {
  SUBSCRIPTION = 'SUBSCRIPTION',
  SETTING_GOAL = 'SETTING_GOAL',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
  OUT_OF_DATE = 'OUT_OF_DATE',
}

export default function ChallengePage() {
  const { slug: challengeSlug } = useParams<Params>();

  const { user, token } = useSelector((state: RootState) => state.auth);
  const { company_id, companies } = user;
  const {
    coros_config, strava_config, garmin_config, polar_config,
  } = useSelector((state: RootState) => state.integration);

  const hasSomeIntegration = !!user?.s_id || !!coros_config?.id || !!strava_config?.id || !!garmin_config?.id || !!polar_config?.id;

  const [state, setState] = useState<ChallengeStatesTypes>(ChallengeStatesTypes.SUBSCRIPTION);
  const [challenge, setChallenge] = useState<ChallengeType>();
  const [challengeDetail, setChallengeDetail] = useState<ChallengeDetailType>();

  const [showIntegrationCard, setShowIntegrationCard] = useState(true);
  const [loading, setLoading] = useState(true);

  const renderCurrentState = () => {
    const stateMap: Record<ChallengeStatesTypes, JSX.Element> = {
      [ChallengeStatesTypes.SUBSCRIPTION]: (
        <ChallengeSubscription challengeDetail={challengeDetail!} />
      ),
      [ChallengeStatesTypes.SETTING_GOAL]: (
        <ChallengeGoal
          userId={user.id}
          setChallenge={setChallenge}
          challenge={challenge}
          action={() => setState(ChallengeStatesTypes.IN_PROGRESS)}
        />
      ),
      [ChallengeStatesTypes.IN_PROGRESS]: (
        <ChallengeProgress
          challenge={challenge!}
          challengeDetail={challengeDetail!}
          setChallenge={setChallenge}
        />
      ),
      [ChallengeStatesTypes.OUT_OF_DATE]: (
        <ChallengeProgress
          challenge={challenge!}
          challengeDetail={challengeDetail!}
          setChallenge={setChallenge}
        />
      ),
      [ChallengeStatesTypes.FINISHED]: (
        <ChallengeProgress
          challenge={challenge!}
          challengeDetail={challengeDetail!}
          setChallenge={setChallenge}
        />
      ),
    };

    return stateMap[state] ?? (
      <ChallengeSubscription challengeDetail={challengeDetail!} />
    );
  };

  const checkIfCompanyChallenge = (challengeDetailData: ChallengeDetailType) => {
    if (challengeDetailData && !challenge && (challengeDetailData.isCompanyChallenge
        && (company_id === challengeDetailData.companyId
        || companies?.some((company) => company.id === challengeDetailData.companyId)))) {
      return true;
    }
    return false;
  };

  const createCompanyChallenge = async (challengeDetailData: ChallengeDetailType) => {
    if (checkIfCompanyChallenge(challengeDetailData)) {
      const data = {
        user_id: user.id,
        weeks: 0,
        slug: challengeDetailData.slug,
        status: ChallengeStatesTypes.IN_PROGRESS,
      };

      await coreApi.post('challenge', data)
        .then((response) => {
          if (response.data) {
            setChallenge(response.data);
            setState(ChallengeStatesTypes.IN_PROGRESS);
            toast.success('Desafio iniciado com sucesso!');
          }
        })
        .catch(() => {
          toast.error('Erro ao iniciar o desafio');
        });
    }
  };

  const fetchChallengeDetail = async (): Promise<ChallengeDetailType> => {
    try {
      const response = await api2.get(`/challenge-detail/slug/${challengeSlug}`);
      setChallengeDetail(response.data);
      return response.data;
    } catch (error) {
      toast.error('Erro ao carregar os detalhes do desafio');
      throw new Error('Failed to fetch challenge detail');
    }
  };

  const fetchChallenge = async (): Promise<ChallengeType> => {
    try {
      const response = await coreApi.get(`challenge/${challengeSlug}/${user.id}`);
      setChallenge(response.data);
      setState(response.data.status);
      return response.data;
    } catch {
      toast.error('Erro ao carregar o desafio');
      throw new Error('Failed to fetch challenge');
    }
  };

  const handleChallengeInfo = async () => {
    setLoading(true);

    const challengeDetailData = await fetchChallengeDetail();

    if (!user.id && !token) {
      setState(ChallengeStatesTypes.SUBSCRIPTION);
      setLoading(false);
      return;
    }

    if (!challengeDetailData) {
      return;
    }

    if (challengeDetailData.isCompanyChallenge) {
      const challengeData = await fetchChallenge();
      if (!challengeData) {
        await createCompanyChallenge(challengeDetailData);
      }
    } else {
      await api.get(`challenge/verify/${user.id}/${challengeSlug}`);
      const challengeData = await fetchChallenge();
      if (!challengeData) {
        setState(ChallengeStatesTypes.SUBSCRIPTION);
      }
    }
    setLoading(false);
  };
  useEffect(() => {
    handleChallengeInfo();
  }, []);

  React.useEffect(() => {
    if (state === ChallengeStatesTypes.SUBSCRIPTION) {
      setShowIntegrationCard(false);
    }
  }, []);

  return (
    <Container>
      <BackArrow text={challengeDetail?.title || 'Desafio'} bigger backTo="/club/desafios" />
      <Content>
        {loading
          && (
            <LoadingContainer>
              <Loader color="white" />
            </LoadingContainer>
          )}
        {!loading && (
          <Card>
            {challengeDetail && (
              <ChallengeHeader
                challengeDetail={challengeDetail}
                status={state}
                edit={() => setState(ChallengeStatesTypes.SETTING_GOAL)}
              />
            )}
            {challengeSlug && challengeDetail && renderCurrentState()}
          </Card>
        )}
      </Content>
      {showIntegrationCard && <IntegrationCard action={() => setShowIntegrationCard(false)} isIntegrationActive={hasSomeIntegration} />}
    </Container>
  );
}
